import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bal, errorText, fmtDate, fyToDate, Ledger, ledgersApi, LedgerStatement, money, natureLabel, Range, reportsApi } from '../accounting.api';
import BooksHeader, { RangeFields } from '../components/BooksHeader';
import { useBooksCompany } from '../components/CompanySelect';
import LedgerStatementTable from '../components/LedgerStatementTable';
import VoucherViewer from '../components/VoucherViewer';
import { SummaryTiles } from './CashBankBookPage';

/**
 * One ledger's statement for a range. The ledger and range live in the address
 * (?ledgerId=&from=&to=), so the Ledgers list and the Trial Balance can link straight here.
 */
export default function LedgerBookPage() {
  const { companies, companyId, setCompanyId, loaded } = useBooksCompany();
  const [params, setParams] = useSearchParams();
  const fy = useMemo(fyToDate, []);
  const ledgerId = Number(params.get('ledgerId')) || null;
  const range: Range = { from: params.get('from') || fy.from, to: params.get('to') || fy.to };

  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [statement, setStatement] = useState<LedgerStatement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);

  const update = (p: { ledgerId?: number | null; from?: string; to?: string }) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, String(v)) : next.delete(k)));
    setParams(next, { replace: true });
  };

  useEffect(() => {
    setLedgers([]);
    if (!companyId) return;
    ledgersApi
      .list(companyId)
      .then(setLedgers)
      .catch((e) => toast.error(errorText(e, 'Could not load ledgers', 'ACCOUNTING_LEDGERS_FAILED')));
  }, [companyId]);

  const { from, to } = range;
  const load = useCallback(() => {
    setStatement(null);
    setError(null);
    if (!companyId || !ledgerId || !from || !to) return;
    setLoading(true);
    reportsApi
      .ledger(companyId, ledgerId, { from, to })
      .then(setStatement)
      .catch((e) => setError(errorText(e, 'Could not load the ledger', 'ACCOUNTING_LEDGER_BOOK_FAILED')))
      .finally(() => setLoading(false));
  }, [companyId, ledgerId, from, to]);

  useEffect(load, [load]);

  const chosen = ledgers.find((l) => l.id === ledgerId) ?? null;

  return (
    <Stack gap={3}>
      <BooksHeader title='LEDGER BOOK' companies={companies} companyId={companyId} onCompany={setCompanyId} loaded={loaded}>
        <Autocomplete
          size='small'
          options={ledgers}
          value={chosen}
          onChange={(_, l) => update({ ledgerId: l?.id ?? null })}
          getOptionLabel={(l) => l.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          groupBy={(l) => l.groupName}
          sx={{ minWidth: 260, flex: { xs: 1, md: 'none' } }}
          renderInput={(p) => <TextField {...p} label='Ledger' />}
        />
        <RangeFields value={range} onChange={(r) => update(r)} />
      </BooksHeader>

      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}
      {!ledgerId && companyId && <Alert severity='info'>Choose a ledger to see its statement.</Alert>}

      {statement && (
        <>
          <Box>
            <Typography variant='h6' fontWeight={700}>
              {statement.ledgerName}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {statement.groupName} · {natureLabel(statement.nature)} · {fmtDate(statement.from)} to {fmtDate(statement.to)}
            </Typography>
          </Box>
          <SummaryTiles
            items={[
              ['Opening', bal(statement.opening)],
              ['Debit', money(statement.totalDebit)],
              ['Credit', money(statement.totalCredit)],
              ['Closing', bal(statement.closing)],
            ]}
          />
          <LedgerStatementTable statement={statement} onVoucher={setViewing} />
        </>
      )}

      <VoucherViewer companyId={companyId} voucherId={viewing} onClose={() => setViewing(null)} onChanged={load} />
    </Stack>
  );
}
